import { useState } from "react";

import { Box, Button, Chip, Divider, Typography } from "@mui/material";

import { formatDate, ucFirstLetters } from "@aclymatepackages/formatters";

import SmartUploadFlow from "./SmartUploadFlow";
import SmartUploadReviewDialog from "./SmartUploadReviewDialog";
import { REVIEWABLE_TASK_STATUSES } from "./reviewLineGates";
import { isReviewDrivenCsvFamily } from "../../../helpers/smartUploadLineRequirements";

import { useCollectionDataListener } from "../../../helpers/firebase";
import usePermissions from "../../../helpers/hooks/usePermissions";
import { getAccountId } from "../../../helpers/accountHelpers";

const BATCH_STATUS_LABELS = {
  ready_for_review: "Ready to review",
  partially_reviewed: "Review in progress",
  bulk_committing: "Confirming…",
  finished: "Reviewed"
};

const PastBatchRow = ({ batch, onReview }) => {
  const {
    batchId,
    status,
    created_at: createdAt,
    document_family: documentFamily
  } = batch;
  const isReviewable = REVIEWABLE_TASK_STATUSES.includes(status);
  // A finished batch is still reachable (#42) -- mirrors AccountActivity.js's
  // own isFinishedSmartUpload: "Open" re-enters the same review dialog, which
  // renders read-only except for the manual-entry CTA on eligible rows.
  const isFinishedSmartUpload = status === "finished" && !isReviewDrivenCsvFamily(documentFamily);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 1,
        py: 1
      }}
    >
      <Typography variant="body2">
        {createdAt ? formatDate(createdAt) : batchId}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Chip
          size="small"
          label={
            BATCH_STATUS_LABELS[status] || ucFirstLetters(status || "pending")
          }
        />
        {isReviewable && (
          <Button size="small" onClick={onReview}>
            Review
          </Button>
        )}
        {isFinishedSmartUpload && (
          <Button size="small" onClick={onReview}>
            Open
          </Button>
        )}
      </Box>
    </Box>
  );
};

// Mounted by both the Offices slideout (OfficeDetailsSlider.js) and the
// existing Settings VehicleDetailsSlider (settings/Vehicles.js) -- see
// ticket 9zFp7uWgIg1zwzgQT8ia's spec, Technical Design. It composes the same
// SmartUploadFlow the general (non-entity-scoped) upload surface uses,
// parameterized with a real entityContext, rather than calling a separate
// backend path -- #0's own spec confirms this call-path boundary (Cross-
// Ticket Contracts).
const EntitySmartUploadTab = ({ entityType, entityId, entityName }) => {
  // AddEmissionsMenu.js (the general upload entry point) gates its own
  // SmartUploadFlow the same way -- the backend's own start-batch endpoint
  // only checks the caller is a member of the company (requireCompany), not
  // that they hold edit permission, so a read-only viewer with no gate here
  // would get a fully working upload dropzone (self-review finding).
  const { canEdit } = usePermissions();

  // A live onSnapshot listener, not useCachedDisplayData's one-time-fetch
  // SWR cache -- the batch just started via SmartUploadFlow writes its task
  // doc through the backend controller, not through useCachedFirebaseCrud,
  // so nothing would invalidate a cached one-time fetch and the just-
  // uploaded batch would never appear here without a full remount
  // (self-review finding). AccountActivity.js's own tasks list uses this
  // same hook for the identical reason.
  const [pastBatches] = useCollectionDataListener("tasks", [
    ["type", "==", "smart-upload"],
    ["entity_context.entity_id", "==", entityId]
  ]);

  // Bumped on Close so a finished/errored batch's progress view unmounts and
  // a fresh SmartUploadFlow mounts in its place -- otherwise `taskDocId`
  // never clears and a second batch could never be started without leaving
  // and reopening the slideout (self-review finding).
  const [uploadInstanceKey, setUploadInstanceKey] = useState(0);
  const [reviewTaskId, setReviewTaskId] = useState(null);

  const entityContext = { entity_type: entityType, entity_id: entityId };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {canEdit ? (
        <SmartUploadFlow
          key={uploadInstanceKey}
          entityContext={entityContext}
          entityName={entityName}
          onClose={() => setUploadInstanceKey((key) => key + 1)}
        />
      ) : (
        <Typography variant="body2" color="textSecondary">
          You don&apos;t have permission to upload documents here.
        </Typography>
      )}
      {!!pastBatches.length && (
        <>
          <Divider />
          <Typography variant="subtitle2">
            Documents previously uploaded to {entityName}
          </Typography>
          {pastBatches.map((batch) => (
            <PastBatchRow
              batch={batch}
              key={batch.id}
              onReview={() => setReviewTaskId(batch.id)}
            />
          ))}
        </>
      )}
      {reviewTaskId && (
        <SmartUploadReviewDialog
          companyId={getAccountId()}
          taskDocId={reviewTaskId}
          onClose={() => setReviewTaskId(null)}
        />
      )}
    </Box>
  );
};
export default EntitySmartUploadTab;
